import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getOrgTasksQueryKey } from "../../config";
import { getOrgTasks, TaskScope, UnifiedTask } from "../../services";

export const getOrgTasksParams = ({
  orgId,
  scope = "mine",
  enabled = true,
  placeholderData,
}: {
  orgId: string;
  scope?: TaskScope;
  enabled?: boolean;
  placeholderData?: UnifiedTask[] | (() => UnifiedTask[] | undefined);
}) => {
  return {
    queryKey: getOrgTasksQueryKey(orgId, scope),
    queryFn: async (): Promise<UnifiedTask[]> => {
      const { data } = await getOrgTasks(orgId, scope);
      return data ?? [];
    },
    enabled,
    staleTime: 1000 * 30, // 30 seconds
    placeholderData,
  };
};

export const useGetOrgTasks = ({
  orgId,
  scope = "mine",
  enabled = true,
  placeholderData,
  placeholderTaskId,
  placeholderScopes,
}: {
  orgId: string;
  scope?: TaskScope;
  enabled?: boolean;
  placeholderData?: UnifiedTask[] | (() => UnifiedTask[] | undefined);
  placeholderTaskId?: string;
  placeholderScopes?: TaskScope[];
}) => {
  const queryClient = useQueryClient();
  const placeholderDataFromCache = useMemo(() => {
    if (!orgId || !placeholderTaskId) return undefined;

    return () => {
      const scopesToCheck =
        placeholderScopes && placeholderScopes.length > 0
          ? placeholderScopes
          : (["all", "mine"] as TaskScope[]);

      for (const scopeToCheck of scopesToCheck) {
        const cachedTasks = queryClient.getQueryData<UnifiedTask[]>(
          getOrgTasksQueryKey(orgId, scopeToCheck)
        );
        const cachedTask = cachedTasks?.find(
          (taskToCheck) =>
            taskToCheck.id === placeholderTaskId ||
            taskToCheck.taskKey === placeholderTaskId
        );
        if (cachedTask) return [cachedTask];
      }

      return undefined;
    };
  }, [orgId, placeholderScopes, placeholderTaskId, queryClient]);

  return useQuery(
    getOrgTasksParams({
      orgId,
      scope,
      enabled,
      placeholderData: placeholderData ?? placeholderDataFromCache,
    })
  );
};
